import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cashRegisterService } from '@/services/cashRegisterService';
import { useToast } from '@/composables/useToast';
import { formatCurrency } from '@/utils/priceHelpers';
import { formatDateTime as formatDate } from '@/utils/dateHelpers';
import type {
  Session,
  Transaction,
  SessionSummary,
  PaymentBreakdown,
  SessionCut,
  TransactionPagination
} from '@/components/cashRegister/sessionDetailsTypes';

export function useSessionDetails() {
  const { t, te } = useI18n();
  const toast = useToast();

  // State
  const isLoading = ref(false);
  const session = ref<Session | null>(null);
  const transactions = ref<Transaction[]>([]);
  const sessionSummary = ref<SessionSummary | null>(null);
  const paymentBreakdown = ref<PaymentBreakdown | null>(null);
  const lastCut = ref<SessionCut | null>(null);
  const lastCutLoading = ref(false);

  // Transaction filtering and pagination
  const transactionSearchTerm = ref('');
  const transactionTypeFilter = ref('');
  const transactionPagination = ref<TransactionPagination>({
    page: 1,
    limit: 10,
    offset: 0
  });

  // Computed
  const filteredTransactions = computed(() => {
    let filtered = transactions.value;

    if (transactionSearchTerm.value.trim()) {
      const searchTerm = transactionSearchTerm.value.toLowerCase();
      filtered = filtered.filter(transaction =>
        transaction.description.toLowerCase().includes(searchTerm)
      );
    }

    if (transactionTypeFilter.value) {
      filtered = filtered.filter(transaction =>
        transaction.transaction_type === transactionTypeFilter.value
      );
    }

    return filtered;
  });

  const paginatedTransactions = computed(() => {
    const start = transactionPagination.value.offset;
    const end = start + transactionPagination.value.limit;
    return filteredTransactions.value.slice(start, end);
  });

  const totalTransactionPages = computed(() => {
    return Math.ceil(filteredTransactions.value.length / transactionPagination.value.limit);
  });

  // Helper functions
  const tr = (key: string, fallback: string) => (te(key) ? t(key) : fallback);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case 'open':
        return t('app.views.cashRegister.statusOpen');
      case 'closed':
        return t('app.views.cashRegister.statusClosed');
      default:
        return status;
    }
  };

  const getTransactionTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'sale':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'expense':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
      case 'cut':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
      case 'opening':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200';
      case 'closing':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
    }
  };

  const translateTransactionType = (type: string) => {
    const key = `app.views.cashRegister.transactionTypes.${type}`;
    return te(key) ? t(key) : type;
  };

  const getPaymentMethodBadgeClass = (method: string) => {
    switch (method) {
      case 'cash':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      case 'card':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
      case 'transfer':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
    }
  };

  const translatePaymentMethod = (method: string) => {
    const key = `app.views.cashRegister.paymentMethods.${method}`;
    return te(key) ? t(key) : method;
  };

  const formatDifference = (actual: number | null | undefined, expected: number | null | undefined) => {
    if (actual === null || actual === undefined || expected === null || expected === undefined) {
      return formatCurrency(0);
    }
    const diff = actual - expected;
    return (diff >= 0 ? '+' : '') + formatCurrency(diff);
  };

  const getDifferenceClass = (actual: number | null | undefined, expected: number | null | undefined) => {
    if (actual === null || actual === undefined || expected === null || expected === undefined) {
      return 'text-gray-600 dark:text-gray-400';
    }
    const diff = actual - expected;
    if (Math.abs(diff) < 0.01) return 'text-green-600 dark:text-green-400'; // Exact match
    if (diff > 0) return 'text-blue-600 dark:text-blue-400'; // Surplus
    return 'text-red-600 dark:text-red-400'; // Shortage
  };

  const getDifferenceTextClass = (actual: number | null | undefined, expected: number | null | undefined) => {
    if (actual === null || actual === undefined || expected === null || expected === undefined) {
      return 'text-gray-500 dark:text-gray-400';
    }
    const diff = actual - expected;
    if (Math.abs(diff) < 0.01) return 'text-green-600 dark:text-green-400';
    if (diff > 0) return 'text-blue-600 dark:text-blue-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getDifferenceLabel = (actual: number | null | undefined, expected: number | null | undefined) => {
    if (actual === null || actual === undefined || expected === null || expected === undefined) {
      return t('app.common.na');
    }
    const diff = actual - expected;
    if (Math.abs(diff) < 0.01) return t('app.views.cashRegister.exactMatch');
    if (diff > 0) return t('app.views.cashRegister.surplus');
    return t('app.views.cashRegister.shortage');
  };

  // API methods
  const loadSessionDetails = async (sessionId: number) => {
    try {
      isLoading.value = true;
      const sessionResponse = await cashRegisterService.getSessionById(sessionId);
      session.value = sessionResponse?.data || sessionResponse || null;

      if (!session.value) {
        toast.showToast(t('app.views.cashRegister.sessionNotFound'), 'error');
        return;
      }

      
      const transactionsResponse = await cashRegisterService.getTransactions(sessionId);
      transactions.value = Array.isArray(transactionsResponse)
        ? transactionsResponse
        : transactionsResponse.data || [];

      try {
        await loadLastCutForSession(sessionId);
        if (lastCut.value) {
          sessionSummary.value = {
            session_id: session.value.id,
            session_number: session.value.session_number || session.value.id,
            opened_at: session.value.opened_at,
            closed_at: session.value.closed_at || null,
            total_sales: lastCut.value.total_sales || 0,
            total_expenses: lastCut.value.total_expenses || 0,
            total_refunds: 0,
            total_tips: 0,
            net_cash_flow: (lastCut.value.total_sales || 0) - (lastCut.value.total_expenses || 0),
            total_transactions: lastCut.value.total_transactions || 0,
            payment_breakdown: lastCut.value.payment_breakdown || {
              cash: lastCut.value.cash_sales || 0,
              card: lastCut.value.card_sales || 0,
              digital: lastCut.value.transfer_sales || 0,
              other: 0,
              Omapas: 0,
              Credito: 0,
            },
          };
        } else {
          sessionSummary.value = null;
        }
      } catch (reportError) {
        console.warn('Could not load session report:', reportError);
        sessionSummary.value = null;
      }
    } catch (error: any) {
      console.error('Error loading session details:', error);
      toast.showToast(
        t('app.views.cashRegister.errorLoadingSessionDetails'),
        'error'
      );
    } finally {
      isLoading.value = false;
    }
  };

  const loadLastCutForSession = async (sessionId: number) => {
    try {
      lastCutLoading.value = true;
      const cutData = await cashRegisterService.getLastCut(sessionId);
      lastCut.value = cutData?.data || cutData || null;
    } catch (error) {
      console.error('Error loading last cut for session:', error);
      lastCut.value = null;
    } finally {
      lastCutLoading.value = false;
    }
  };

  const resetTransactionPagination = () => {
    transactionSearchTerm.value = '';
    transactionTypeFilter.value = '';
    transactionPagination.value = {
      page: 1,
      limit: 10,
      offset: 0
    };
  };

  const previousTransactionPage = () => {
    if (transactionPagination.value.page > 1) {
      transactionPagination.value.page--;
      transactionPagination.value.offset =
        (transactionPagination.value.page - 1) * transactionPagination.value.limit;
    }
  };

  const nextTransactionPage = () => {
    if (transactionPagination.value.page < totalTransactionPages.value) {
      transactionPagination.value.page++;
      transactionPagination.value.offset =
        (transactionPagination.value.page - 1) * transactionPagination.value.limit;
    }
  };

  const clearSessionData = () => {
    session.value = null;
    transactions.value = [];
    sessionSummary.value = null;
    paymentBreakdown.value = null;
    lastCut.value = null;
  };

  return {
    // State
    isLoading,
    session,
    transactions,
    sessionSummary,
    paymentBreakdown,
    lastCut,
    lastCutLoading,
    transactionSearchTerm,
    transactionTypeFilter,
    transactionPagination,
    
    // Computed
    filteredTransactions,
    paginatedTransactions,
    totalTransactionPages,
    
    // Helper functions
    tr,
    getStatusBadgeClass,
    translateStatus,
    getTransactionTypeBadgeClass,
    translateTransactionType,
    getPaymentMethodBadgeClass,
    translatePaymentMethod,
    formatDifference,
    getDifferenceClass,
    getDifferenceTextClass,
    getDifferenceLabel,
    
    // Methods
    loadSessionDetails,
    loadLastCutForSession,
    resetTransactionPagination,
    previousTransactionPage,
    nextTransactionPage,
    clearSessionData
  };
}
